import React, { useState } from "react";
import { AppBar, Button, IconButton, Toolbar, Typography, Drawer, List, ListItem, ListItemText, Box } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

// Çeviri İçerikleri
const translations = {
  tr: {
    home: "Anasayfa",
    about: "Hakkımda",
    portfolio: "Portfolyo",
    hireMe: "İletişime Geç",
  },
  en: {
    home: "Home",
    about: "About",
    portfolio: "Portfolio",
    hireMe: "Contact Me",
  },
};

const Navbar = ({ currentLang = "tr", onLangChange }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const normalizedLang = String(currentLang).toLowerCase();
  const t = translations[normalizedLang] || translations.tr;

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setIsDrawerOpen(open);
  };

  const handleLanguageToggle = () => {
    if (onLangChange) {
      onLangChange(normalizedLang === "tr" ? "en" : "tr");
    }
  };

  const navItems = [
    { label: t.home, id: "hero" },
    { label: t.about, id: "about" },
    { label: t.portfolio, id: "services" },
  ];

  return (
    <AppBar position="fixed" sx={{ backgroundColor: "#f7f8f8", boxShadow: "none", paddingBlock: 1 }}>
      <Toolbar sx={{ justifyContent: { xs: "space-between", sm: "space-around" }, gap: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: "#61a05f",
            fontWeight: "bold",
            fontSize: "20px",
          }}
        >
          <Box
            component="img"
            src="helmet.svg"
            alt="İdris Aktaş Logo - Helmet"
            sx={{ width: "50px", height: 'auto' }}
          />
        </Typography>

        {/* Masaüstü Menü Linkleri */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 4, alignItems: "center" }}>
          {navItems.map((item) => (
            <Typography
              key={item.id}
              variant="H24px"
              color="textPrimary"
              onClick={() => scrollToSection(item.id)}
              sx={{ cursor: "pointer" }} 
            >
              {item.label}
            </Typography>
          ))}
        </Box>

        {/* Sağ Taraf: Dil Seçimi ve İletişim Butonu */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 2 }}>
          {/* Gelişmiş Masaüstü TR / EN Butonu */}
          <Button 
            onClick={handleLanguageToggle}
            startIcon={<LanguageIcon sx={{ color: "#305041" }} />}
            sx={{ 
              color: "#a4b3ac", // Aktif olmayan dilin sönük rengi
              textTransform: "uppercase",
              fontSize: "15px",
              paddingInline: 1
            }}
          >
            <Box component="span" sx={{ fontWeight: normalizedLang === "tr" ? "900" : "400", color: normalizedLang === "tr" ? "#305041" : "inherit" }}>
              TR
            </Box>
            <Box component="span" sx={{ mx: 0.5, color: "#305041" }}>/</Box>
            <Box component="span" sx={{ fontWeight: normalizedLang === "en" ? "900" : "400", color: normalizedLang === "en" ? "#305041" : "inherit" }}>
              EN
            </Box>
          </Button>

          <Button 
            onClick={() => (window.location.href = "mailto:idrissaktass98@gmail.com")}
            variant="contained"
            sx={{
              position: "relative",
              backgroundColor: "#61a05f",
              color: "white",
              textTransform: "none",
              overflow: "hidden",
              "&:hover": { backgroundColor: "#e04e4e" },
              "::before": {
                content: '""',
                position: "absolute",
                top: 50,
                left: "150%",
                width: "300%",
                height: "100%",
                background: "linear-gradient(to right, transparent, rgba(255, 202, 202, 0.6), transparent)",
                zIndex: 1,
                transform: "rotate(45deg)",
                animation: "lightBorder 3.5s infinite",
              },
            }}
          >
            <Typography>{t.hireMe}</Typography>
            <style>
              {`
                @keyframes lightBorder {
                  0% { left: -150%; }
                  100% { left: 150%; }
                }
              `}
            </style>
          </Button>
        </Box>

        {/* Mobil Menü Butonu */}
        <IconButton
          edge="end"
          color="inherit"
          aria-label="menu"
          sx={{ display: { xs: "block", sm: "none" } }}
          onClick={toggleDrawer(true)}
        >
          <MenuIcon sx={{ color: "#61a05f" }} />
        </IconButton>

        {/* Mobil Çekmece (Drawer) */}
        <Drawer anchor="right" open={isDrawerOpen} onClose={toggleDrawer(false)}>
          <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
          >
            <List>
              {navItems.map((item) => (
                <ListItem button key={item.id} onClick={() => scrollToSection(item.id)}>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </List>
            
            <Box textAlign="center" mt={2} display="flex" flexDirection="column" alignItems="center" gap={2}>
              {/* Gelişmiş Mobil TR / EN Butonu */}
              <Button 
                onClick={handleLanguageToggle}
                startIcon={<LanguageIcon sx={{ color: "#305041" }} />}
                sx={{ 
                  color: "#a4b3ac",
                  textTransform: "uppercase",
                  fontSize: "16px",
                  width: "80%"
                }}
              >
                <Box component="span" sx={{ fontWeight: normalizedLang === "tr" ? "900" : "400", color: normalizedLang === "tr" ? "#305041" : "inherit" }}>
                  Türkçe (TR)
                </Box>
                <Box component="span" sx={{ mx: 1, color: "#305041" }}>/</Box>
                <Box component="span" sx={{ fontWeight: normalizedLang === "en" ? "900" : "400", color: normalizedLang === "en" ? "#305041" : "inherit" }}>
                  English (EN)
                </Box>
              </Button>

              <Button 
                onClick={() => (window.location.href = "mailto:idrissaktass98@gmail.com")}
                variant="contained"
                sx={{
                  backgroundColor: "#61a05f",
                  color: "white",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#e04e4e" },
                }}
              >
                {t.hireMe}
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;